import tailwindcssAnimate from "tailwindcss-animate";
import plugin from "tailwindcss/plugin";
import type { PresetsConfig, ThemeConfig } from "tailwindcss/types/config";

// COLORS
const colors = {
  white: "#FFFFFF",
  red: {
    50: "#fceded",
    100: "#f4c6c7",
    200: "#efaaac",
    300: "#e88386",
    400: "#e36b6e",
    500: "#dc464a",
    600: "#c84043",
    700: "#9c3235",
    800: "#792729",
    900: "#5c1d1f",
  },
  pink: {
    50: "#fdebf1",
    100: "#f8c2d4",
    200: "#f5a4c0",
    300: "#f17ba3",
    400: "#ee6191",
    500: "#ea3a75",
    600: "#d5356a",
    700: "#a62953",
    800: "#812040",
    900: "#621831",
  },
  orange: {
    50: "#fcf0eb",
    100: "#f6d1c1",
    200: "#f2bba3",
    300: "#ec9c78",
    400: "#e9895e",
    500: "#e36b36",
    600: "#cf6131",
    700: "#a14c26",
    800: "#7d3b1e",
    900: "#5f2d17",
  },
  yellow: {
    50: "#fef9ec",
    100: "#fdeec3",
    200: "#fce5a6",
    300: "#fbd97d",
    400: "#fad264",
    500: "#f9c73d",
    600: "#e3b538",
    700: "#b18d2b",
    800: "#896d22",
    900: "#69541a",
  },
  blue: {
    50: "#eff0fe",
    100: "#cdcffb",
    200: "#b4b8f9",
    300: "#9298f6",
    400: "#7d84f4",
    500: "#5d65f1",
    600: "#555cdb",
    700: "#4248ab",
    800: "#272a65",
    900: "#0C0F31",
  },
  green: {
    50: "#ecf8f0",
    100: "#c3e9d0",
    200: "#a6deb9",
    300: "#7dcf99",
    400: "#64c585",
    500: "#3db767",
    600: "#38a75e",
    700: "#2b8249",
    800: "#226539",
    900: "#1a4d2b",
  },
  gray: {
    50: "#f5f6f8",
    100: "#dfe2e8",
    200: "#d0d4dd",
    300: "#bbc1cd",
    400: "#adb5c3",
    500: "#99a2b4",
    600: "#8b93a4",
    700: "#6d7380",
    800: "#545963",
    900: "#40444c",
  },
};

// FONT SIZE
const fontSize: ThemeConfig["fontSize"] = {
  body: [
    ".75rem", // 12px
    {
      lineHeight: "1.5",
      letterSpacing: "0em",
    },
  ],
  heading1: [
    "4.5rem", // 72px
    {
      lineHeight: "1.11",
      letterSpacing: "-.01em",
    },
  ],
  heading2: [
    "3.5rem", // 56px
    {
      lineHeight: "1.14",
      letterSpacing: "0em",
    },
  ],
  heading3: [
    "2.5rem", // 40px
    {
      lineHeight: "1.20",
      letterSpacing: ".005em",
    },
  ],
  heading4: [
    "2rem", // 32px
    {
      lineHeight: "1.25",
      letterSpacing: ".005em",
    },
  ],
  heading5: [
    "1.25rem", // 20px
    {
      lineHeight: "1.40",
      letterSpacing: ".005em",
    },
  ],
  heading6: [
    "1rem", // 16px
    {
      lineHeight: "1.50",
      letterSpacing: ".01em",
    },
  ],
  caption: [
    ".75rem", // 12px
    {
      lineHeight: "1.33",
      letterSpacing: ".01em",
    },
  ],
  overline: [
    ".75rem", // 12px
    {
      lineHeight: "1.33",
      letterSpacing: ".075em",
    },
  ],
  button: [
    ".75rem", // 12px
    {
      lineHeight: "1.50",
      letterSpacing: ".04em",
    },
  ],
  "avatar-xs": [
    "7.5px",
    {
      lineHeight: "8.4px",
      letterSpacing: "0.03px",
    },
  ],
  "avatar-sm": [
    "10px",
    {
      lineHeight: "13px",
      letterSpacing: "0",
    },
  ],
  "avatar-md": [
    "15px",
    {
      lineHeight: "19.5px",
      letterSpacing: "0.45px",
    },
  ],
  "avatar-lg": [
    "20px",
    {
      lineHeight: "26px",
      letterSpacing: "0",
    },
  ],
  xs: [
    ".75rem", // 12px
    {
      lineHeight: "1.5",
      letterSpacing: "0em",
    },
  ],
  sm: [
    ".875rem", // 14px
    {
      lineHeight: "1.43",
      letterSpacing: "0em",
    },
  ],
  xl: [
    "1.25rem", // 20px
    {
      lineHeight: "1.40",
      letterSpacing: "0.005em",
    },
  ],
};

// SCREENS
const screens = {
  sm: "320px",
  /* => @media (min-width: 320px) { ... } / Mobile */

  md: "480px",
  /* => @media (min-width: 480px) { ... } / Tablette */

  lg: "768px",
  /* => @media (min-width: 768px) { ... } / Tablette large*/

  xl: "1024px",
  /* => @media (min-width: 1024px) { ... } / Desktop */

  xxl: "1440px",
  /* => @media (min-width: 1440px) { ... } / Grands écrans */
};

// SPACING
const spacing = {
  "0": "0",
  "0.5": "2px",
  "1": "4px",
  "1.5": "6px",
  "2": "8px",
  "2.5": "10px",
  "3": "12px",
  "3.5": "14px",
  "4": "16px",
  "5": "20px",
  "5.5": "24px",
  "6": "32px",
  "6.5": "36px",
  "7": "40px",
  "7.5": "44px",
  "8": "48px",
  "8.5": "56px",
  "9": "64px",
  "10": "96px",
  "11": "128px",
  "12": "136px",
  "13": "152px",
};

// BORDER RADIUS
const borderRadius = {
  none: "0",
  xs: "1px",
  sm: "2px",
  custom: "4px",
  DEFAULT: "6px",
  md: "8px",
  lg: "10px",
  full: "99px",
};
const boxShadow = {
  none: "none",
  sm: "0px 1.333px 2.667px 0px rgba(0, 0, 0, .08)",
  md: ".67px .67px 0 0 rgba(0, 0, 0, 1)",
  "custom-large": "0px 8px 16px 0px rgba(0, 0, 0, .08)",
  "custom-dialog": "0 4px 8px rgba(0, 0, 0, .04), 0 0 4px rgba(0, 0, 0, .04)",
  toaster: "0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
};
const config = {
  darkMode: ["class"],
  safelist: [
    "border-l-2",
    "border-l-green-500",
    "bg-green-gradient",
    "*:bg-opacity-0",
    "text-green-500",
  ],
  theme: {
    extend: {
      margin: {
        sheet: "340px",
      },
      transitionProperty: {
        sheet: "margin-right, transform",
      },
      transitionDuration: {
        sheet: "400ms",
      },
      backgroundImage: {
        "primary-gradient": "linear-gradient(180deg, #E01F5F 0%, #9F1643 100%)",
        "primary-gradient-r":
          "linear-gradient(90deg, #E01F5F 0%, #9F1643 100%)",
        "green-gradient":
          "linear-gradient(90deg, rgba(61,183,103,0.10) 0%, rgba(255,255,255,0.00) 10.9%)",
      },
      fontFamily: {
        poppins: ["Poppins", "sans-serif"],
        sans: ["Poppins", "sans-serif"],
      },
    },
    colors: {
      ...colors,
      primary: colors.pink[500],
      secondary: colors.blue[400],
      error: colors.red[500],
      warning: colors.orange[500],
      success: colors.green[500],
      info: colors.blue[500],
      loading: colors.blue[500],
      neutral: colors.gray[500],
    },
    fontSize,
    screens,
    spacing,
    borderRadius,
    boxShadow,
  },
  plugins: [
    tailwindcssAnimate,
    plugin(({ addBase, theme }) => {
      addBase({
        // Custom scrollbar styles
        ".menu-scroll::-webkit-scrollbar": {
          width: "12px",
          background: "transparent",
        },

        ".menu-scroll::-webkit-scrollbar-thumb": {
          backgroundColor: theme("colors.pink.700"),
          borderRadius: "6px",
          border: "4px solid transparent",
          backgroundClip: "padding-box",
          cursor: "pointer",
        },

        ".menu-scroll::-webkit-scrollbar-thumb:hover": {
          backgroundColor: theme("colors.pink.800"),
        },
        // Custom scrollbar pink.500
        ".pink-scroll::-webkit-scrollbar": {
          width: "9px",
          background: "transparent",
        },
        ".pink-scroll::-webkit-scrollbar-track": {
          background: "transparent",
        },
        ".pink-scroll::-webkit-scrollbar-thumb": {
          backgroundColor: theme("colors.pink.500"),
          borderRadius: "12px",
          border: "3px solid transparent",
          backgroundClip: "padding-box",
          cursor: "pointer",
        },
        ".pink-scroll::-webkit-scrollbar-thumb:hover": {
          backgroundColor: theme("colors.pink.600"),
        },
        // Custom scrollbar table
        ".scroll-table::-webkit-scrollbar": {
          "@apply w-2 h-2 bg-white rounded-md": {},
        },

        ".scroll-table::-webkit-scrollbar-thumb": {
          border: "4px solid transparent",
          "@apply border-2 cursor-pointer bg-pink-400 rounded-md": {},
          "background-clip": "padding-box",
        },

        ".scroll-table::-webkit-scrollbar-thumb:hover": {
          "@apply bg-primary": {},
        },
        ".font-base": {
          "@apply font-poppins font-normal": {},
        },
        ".font-title": {
          "@apply font-poppins font-semibold": {},
        },
        body: {
          "@apply text-body text-gray-900 font-normal antialiased": {},
        },
        h1: {
          "@apply text-heading1 font-bold": {}, // weight 700
        },
        h2: {
          "@apply text-heading2 font-bold": {}, // weight 700
        },
        h3: {
          "@apply text-heading3 font-semibold": {},
        },
        h4: {
          "@apply text-heading4 font-semibold": {},
        },
        h5: {
          "@apply text-heading5 font-semibold": {},
        },
        h6: {
          "@apply text-heading6 font-semibold": {},
        },
        caption: {
          "@apply text-caption font-normal inline-flex": {},
        },
        ".overlined": {
          "@apply text-overline uppercase font-semibold": {},
        },
        button: {
          "@apply text-button font-semibold": {},
        },
        ":focus-visible": {
          "@apply outline-none": {},
        },
      });
    }),
    plugin(({ matchUtilities, theme }) => {
      matchUtilities(
        {
          "text-size": (value) => ({
            fontSize: value,
          }),
        },
        { values: theme("fontSize") },
      );
    }),
    plugin(({ addUtilities }) => {
      addUtilities({
        ".bg-stripes-gray": {
          background: `repeating-linear-gradient(
            -45deg,
            #d0d4dd,
            #d0d4dd 2px,
            #f5f6f8 2px,
            #f5f6f8 4px
          )`,
        },
      });
    }),
  ],
} satisfies PresetsConfig;

export default config;
